// src/components/SubscriptionModal.tsx
// Modal para suscribirse con Mercado Pago

import React, { useState, useEffect } from 'react';
import { createMercadoPagoPreference, getSubscriptionStatus } from '../services/api';
import { AlertTriangle, Rocket, CreditCard } from 'lucide-react';

// Fallback si no se puede leer el precio de la cuenta (precio de alta nueva).
const DEFAULT_MONTHLY_PRICE = 20000;

interface SubscriptionModalProps {
  isOpen: boolean;
  onClose: () => void;
  daysRemaining?: number;
  isExpired?: boolean;
}

const SubscriptionModal: React.FC<SubscriptionModalProps> = ({
  isOpen,
  onClose,
  daysRemaining = 0,
  isExpired = false,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Precio real de la cuenta (grandfathered): se lee del backend. null = cargando.
  const [monthlyPrice, setMonthlyPrice] = useState<number | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    getSubscriptionStatus()
      .then((s) => {
        if (!cancelled) {
          setMonthlyPrice(typeof s.monthly_price === 'number' ? s.monthly_price : DEFAULT_MONTHLY_PRICE);
        }
      })
      .catch(() => {
        if (!cancelled) setMonthlyPrice(DEFAULT_MONTHLY_PRICE);
      });
    return () => { cancelled = true; };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubscribe = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await createMercadoPagoPreference();
      
      if (response.init_point) {
        // Redirigir a Mercado Pago
        window.location.href = response.init_point;
      } else {
        throw new Error('No se pudo generar el link de pago');
      }
    } catch (err: any) {
      console.error('Error al crear preferencia de MP:', err);
      setError(err.message || 'Error al procesar el pago');
      setLoading(false);
    }
  };

  // No permitir cerrar si está expirado (bloqueo)
  const canClose = !isExpired;

  return (
    <div className="fixed inset-0 z-[300] overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
      {/* Overlay */}
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div 
          className="fixed inset-0 bg-gray-900 bg-opacity-75 transition-opacity" 
          aria-hidden="true"
          onClick={canClose ? onClose : undefined}
        />

        {/* Center modal */}
        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          {/* Header */}
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0 bg-white rounded-full p-2">
                  <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-white" id="modal-title">
                  {isExpired ? <span className="flex items-center gap-2"><AlertTriangle className="w-5 h-5" /> Periodo Expirado</span> : <span className="flex items-center gap-2"><Rocket className="w-5 h-5" /> Suscribirse a ConsultAR</span>}
                </h3>
              </div>
              {canClose && (
                <button
                  onClick={onClose}
                  className="text-white hover:text-gray-200 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          </div>

          {/* Body */}
          <div className="bg-white px-6 py-6">
            {/* Alert si está expirado */}
            {isExpired && (
              <div className="mb-4 bg-red-50 border-l-4 border-red-500 p-4 rounded">
                <div className="flex items-center">
                  <svg className="w-5 h-5 text-red-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <p className="text-sm text-red-700 font-medium">
                    Tu período de prueba ha expirado. Suscríbete para continuar usando ConsultAR.
                  </p>
                </div>
              </div>
            )}

            {/* Info del trial si no está expirado */}
            {!isExpired && daysRemaining > 0 && (
              <div className="mb-4 bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
                <p className="text-sm text-blue-700">
                  <span className="font-semibold">Te quedan {daysRemaining} días de prueba.</span> Suscríbete ahora y asegura tu acceso sin interrupciones.
                </p>
              </div>
            )}

            {/* Plan details */}
            <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-lg p-6 mb-6 border border-indigo-200">
              <div className="text-center mb-4">
                <h4 className="text-2xl font-bold text-gray-900 mb-2">Plan Mensual</h4>
                <div className="flex items-baseline justify-center">
                  <span className="text-5xl font-extrabold text-indigo-600">
                    {monthlyPrice !== null ? `$${monthlyPrice.toLocaleString('es-AR')}` : '$…'}
                  </span>
                  <span className="text-xl text-gray-600 ml-2">/mes</span>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-start">
                  <svg className="w-6 h-6 text-green-500 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <div>
                    <p className="font-semibold text-gray-900">Turnos Ilimitados</p>
                    <p className="text-sm text-gray-600">Gestiona todos los turnos que necesites</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <svg className="w-6 h-6 text-green-500 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <div>
                    <p className="font-semibold text-gray-900">250 Mensajes WhatsApp/mes</p>
                    <p className="text-sm text-gray-600">Confirmaciones, recordatorios y avisos automáticos</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <svg className="w-6 h-6 text-green-500 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <div>
                    <p className="font-semibold text-gray-900">Agenda Online 24/7</p>
                    <p className="text-sm text-gray-600">Tus pacientes reservan turnos cuando quieran</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <svg className="w-6 h-6 text-green-500 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <div>
                    <p className="font-semibold text-gray-900">Historias Clínicas Digitales</p>
                    <p className="text-sm text-gray-600">Fichas médicas con adjuntos, dibujos y compartir entre profesionales</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <svg className="w-6 h-6 text-green-500 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <div>
                    <p className="font-semibold text-gray-900">Gestión de Secretaria</p>
                    <p className="text-sm text-gray-600">Delegá la agenda a tu asistente con acceso completo</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <svg className="w-6 h-6 text-green-500 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <div>
                    <p className="font-semibold text-gray-900">Cobros con Mercado Pago</p>
                    <p className="text-sm text-gray-600">Acepta pagos y señas online de forma segura</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Error message */}
            {error && (
              <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            {/* CTA Button */}
            <button
              onClick={handleSubscribe}
              disabled={loading}
              className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-4 px-6 rounded-lg font-bold text-lg hover:from-indigo-700 hover:to-purple-700 transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
            >
              {loading ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin h-5 w-5 mr-3" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Procesando...
                </span>
              ) : (
                'Suscribirse con Mercado Pago'
              )}
            </button>

            {/* Footer info */}
            <div className="mt-4 text-center">
              <p className="text-xs text-gray-500">
                Al suscribirte aceptas nuestros términos y condiciones. Puedes cancelar en cualquier momento.
              </p>
              <div className="mt-2 flex items-center justify-center space-x-2">
                <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                <p className="text-xs text-gray-500">Pago 100% seguro con Mercado Pago</p>
              </div>
            </div>
          </div>

          {/* Only show close button if not expired */}
          {canClose && (
            <div className="bg-gray-50 px-6 py-4">
              <button
                onClick={onClose}
                className="w-full text-gray-700 hover:text-gray-900 font-medium py-2 transition-colors"
              >
                Recordarme después
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SubscriptionModal;